import { api, observeRequests } from '@/api/client';
import { getConfig } from '@/config/config';
import { screenNameFor } from './screenNames';

export { screenNameFor } from './screenNames';

/**
 * Usage telemetry for the admin statistics (FR-048, WEB-028), the web
 * counterpart of mobile/src/services/analytics.ts: one session while a signed
 * in member has the app in front, screens and features batched, a ping so a
 * quiet session is not mistaken for a crash, and the duration at the end.
 * Sessions are reported with `platform: "web"`.
 */
type EventKind = 'screen' | 'feature' | 'error' | 'crash';
interface QueuedEvent {
  kind: EventKind;
  name: string;
  at: string;
}

const MAX_BATCH = 100;
const MAX_QUEUED = 200;
const MAX_NAME = 120;

let sessionId: string | null = null;
let startedAtMs = 0;
let queue: QueuedEvent[] = [];
let lastScreen: string | null = null;
let starting: Promise<void> | null = null;

const newSessionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 8)}`;

function enqueue(kind: EventKind, name: string) {
  if (!sessionId) return;
  queue.push({ kind, name: name.slice(0, MAX_NAME), at: new Date().toISOString() });
  if (queue.length > MAX_QUEUED) queue = queue.slice(queue.length - MAX_QUEUED);
}

/** Requests that mean the member used a feature — the same list as Android's analytics. */
const FEATURE_ROUTES: [string, RegExp, string][] = [
  ['POST', /^\/posts$/, 'create_post'],
  ['POST', /^\/posts\/[^/]+\/commitments$/, 'ask_for_help'],
  ['POST', /^\/connections\/scan$/, 'scan_qr'],
  ['POST', /^\/promotions$/, 'create_promotion'],
  ['POST', /^\/promotions\/[^/]+\/redemptions$/, 'redeem_promotion'],
  ['POST', /^\/events$/, 'host_event'],
  ['POST', /^\/events\/[^/]+\/interest$/, 'event_interest'],
  ['POST', /^\/commitments\/[^/]+\/kudos$/, 'give_kudos'],
  ['POST', /^\/members\/[^/]+\/vouch$/, 'vouch'],
  ['PATCH', /^\/accounts\/me\/profile$/, 'edit_profile'],
  ['POST', /^\/media$/, 'upload_media'],
];

export function featureForRequest(method: string, path: string): string | null {
  const clean = path.split('?')[0] ?? path;
  return FEATURE_ROUTES.find(([m, pattern]) => m === method && pattern.test(clean))?.[2] ?? null;
}

export interface DeviceInput {
  userAgent: string;
  width: number;
  height: number;
  touch: boolean;
}

export interface WebDevice {
  platform: 'web';
  deviceType: 'phone' | 'tablet' | 'desktop';
  osVersion?: string;
  deviceModel?: string;
  screenWidth: number;
  screenHeight: number;
}

function osOf(ua: string): string | undefined {
  let m: RegExpMatchArray | null;
  if ((m = ua.match(/iPhone OS (\d+)_(\d+)/)) || (m = ua.match(/CPU OS (\d+)_(\d+)/)))
    return `iOS ${m[1]}.${m[2]}`;
  if ((m = ua.match(/Android (\d+(?:\.\d+)?)/))) return `Android ${m[1]}`;
  if ((m = ua.match(/Windows NT (\d+)\.\d+/)))
    return m[1] === '10' ? 'Windows 10' : `Windows NT ${m[1]}`;
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return undefined;
}

function browserOf(ua: string): string | undefined {
  const rules: [RegExp, string][] = [
    [/Edg(?:A|iOS)?\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
    [/(?:Firefox|FxiOS)\/(\d+)/, 'Firefox'],
    [/(?:Chrome|CriOS)\/(\d+)/, 'Chrome'],
    [/Version\/(\d+)(?:\.\d+)*.*Safari/, 'Safari'],
  ];
  for (const [pattern, name] of rules) {
    const m = ua.match(pattern);
    if (m) return `${name} ${m[1]}`;
  }
  return undefined;
}

/** What the admin device statistics show for this browser (osVersion = OS, deviceModel = "<Browser> <major>"). */
export function describeDevice({ userAgent, width, height, touch }: DeviceInput): WebDevice {
  const shortSide = Math.min(width, height);
  const deviceType = !touch ? 'desktop' : shortSide >= 600 ? 'tablet' : 'phone';
  const device: WebDevice = {
    platform: 'web',
    deviceType,
    screenWidth: width,
    screenHeight: height,
  };
  const os = osOf(userAgent);
  const browser = browserOf(userAgent);
  if (os) device.osVersion = os;
  if (browser) device.deviceModel = browser;
  return device;
}

function currentDevice(): WebDevice {
  return describeDevice({
    userAgent: navigator.userAgent,
    width: Math.round(window.screen?.width || window.innerWidth),
    height: Math.round(window.screen?.height || window.innerHeight),
    touch:
      navigator.maxTouchPoints > 0 || window.matchMedia?.('(pointer: coarse)').matches === true,
  });
}

export const telemetry = {
  currentSessionId: (): string | null => sessionId,

  startSession(): Promise<void> {
    if (sessionId) return Promise.resolve();
    if (starting) return starting;
    const id = newSessionId();
    starting = api
      .post('/telemetry/sessions', { id, appVersion: __APP_VERSION__, ...currentDevice() })
      .then(() => {
        sessionId = id;
        startedAtMs = Date.now();
        queue = [];
        lastScreen = null;
      })
      .catch(() => undefined) // statistics must never get in the member's way
      .finally(() => {
        starting = null;
      });
    return starting;
  },

  /** Records the screen for a route change; the same screen twice in a row counts once. */
  trackRoute(pathname: string): void {
    const name = screenNameFor(pathname);
    if (!name || name === lastScreen) return;
    lastScreen = name;
    enqueue('screen', name);
  },

  trackFeature: (name: string) => enqueue('feature', name),
  trackError: (name: string) => enqueue('error', name),

  async flush(): Promise<void> {
    if (!sessionId || queue.length === 0) return;
    const batch = queue.slice(0, MAX_BATCH);
    try {
      await api.post('/telemetry/events', { sessionId, events: batch });
      queue = queue.slice(batch.length);
    } catch {
      // kept for the next try
    }
  },

  async ping(): Promise<void> {
    if (!sessionId) return;
    await api.post(`/telemetry/sessions/${sessionId}/ping`).catch(() => undefined);
  },

  async endSession(): Promise<void> {
    if (!sessionId) return;
    const id = sessionId;
    const durationSec = Math.max(0, Math.min(86400, Math.round((Date.now() - startedAtMs) / 1000)));
    await telemetry.flush();
    sessionId = null;
    queue = [];
    lastScreen = null;
    await api.post(`/telemetry/sessions/${id}/end`, { durationSec }).catch(() => undefined);
  },

  /**
   * The page is going away (tab closed or hidden on a phone): send what is
   * left without waiting, with `keepalive` so the browser finishes the request.
   */
  endSessionOnUnload(): void {
    if (!sessionId) return;
    const id = sessionId;
    const durationSec = Math.max(0, Math.min(86400, Math.round((Date.now() - startedAtMs) / 1000)));
    const { apiUrl } = getConfig();
    const send = (path: string, body: unknown) =>
      fetch(`${apiUrl}${path}`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Kindra-Client': 'web' },
        body: JSON.stringify(body),
      }).catch(() => undefined);
    if (queue.length > 0)
      void send('/telemetry/events', { sessionId: id, events: queue.slice(0, MAX_BATCH) });
    void send(`/telemetry/sessions/${id}/end`, { durationSec });
    sessionId = null;
    queue = [];
    lastScreen = null;
  },

  /** Forgets everything without telling the server (tests, sign-out). */
  async reset(): Promise<void> {
    sessionId = null;
    queue = [];
    lastScreen = null;
    starting = null;
  },
};

observeRequests((method, path) => {
  const feature = featureForRequest(method, path);
  if (feature) telemetry.trackFeature(feature);
});

const FLUSH_EVERY_MS = 20_000;
const PING_EVERY_TICKS = 3;

/**
 * Runs telemetry for a signed-in member: a session while the page is visible,
 * ended when it is hidden or closed (as Android does on background). Returns a stop function.
 */
export function startTelemetry(): () => void {
  void telemetry.startSession();
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void telemetry.startSession();
    else telemetry.endSessionOnUnload();
  };
  const onPageHide = () => telemetry.endSessionOnUnload();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  let ticks = 0;
  const timer = window.setInterval(() => {
    ticks += 1;
    void telemetry.flush();
    if (ticks % PING_EVERY_TICKS === 0) void telemetry.ping();
  }, FLUSH_EVERY_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.clearInterval(timer);
    void telemetry.endSession();
  };
}
